import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Initialize auth state — intentionally runs once on mount only
  useEffect(() => {
    checkAuthState();
  }, []); // eslint-disable-line

  // Configure axios defaults
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    let isRefreshing = false;
    let failedQueue = [];

    const processQueue = (error, token = null) => {
      failedQueue.forEach(prom => {
        if (error) {
          prom.reject(error);
        } else {
          prom.resolve(token);
        }
      });
      failedQueue = [];
    };

    // Response interceptor to handle token expiration
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Skip refresh for login/signup/refresh endpoints
        if (
          originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/signup') ||
          originalRequest.url?.includes('/auth/refresh')
        ) {
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (isRefreshing) {
            // Queue requests while refreshing
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then(token => {
                originalRequest.headers['Authorization'] = 'Bearer ' + token;
                return axios(originalRequest);
              })
              .catch(err => Promise.reject(err));
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (!refreshToken) {
              throw new Error('No refresh token');
            }

            const response = await axios.post(`${API}/auth/refresh`, {
              refresh_token: refreshToken
            });

            const { access_token, refresh_token: newRefreshToken } = response.data;
            
            localStorage.setItem('access_token', access_token);
            localStorage.setItem('refresh_token', newRefreshToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
            
            // Get updated user info
            const userResponse = await axios.get(`${API}/auth/me`);
            setUser(userResponse.data);
            
            processQueue(null, access_token);
            
            // Retry original request
            originalRequest.headers['Authorization'] = 'Bearer ' + access_token;
            return axios(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            logout();
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [user]);

  const checkAuthState = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Verify token and get user info
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      // Fetch subscription status (will be called by components as needed)
      // Removed automatic call to prevent performance issues
    } catch (error) {
      // Token invalid, try to refresh
      const refreshed = await refreshToken();
      if (!refreshed) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        delete axios.defaults.headers.common['Authorization'];
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) return false;

      const response = await axios.post(`${API}/auth/refresh`, {
        refresh_token: refreshToken
      });

      const { access_token, refresh_token: newRefreshToken } = response.data;
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', newRefreshToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Get updated user info
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);
      
      return true;
    } catch (error) {
      return false;
    }
  };

  const signup = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/signup`, userData);
      toast.success('Account created successfully! You can now login.');
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Signup failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const login = async (credentials) => {
    try {
      // Clear any existing tokens first to ensure clean slate
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      delete axios.defaults.headers.common['Authorization'];
      
      const response = await axios.post(`${API}/auth/login`, credentials);
      const { access_token, refresh_token } = response.data;
      
      // Store NEW tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      // Set authorization header with new token
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Also set cache-control to prevent caching
      axios.defaults.headers.common['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      axios.defaults.headers.common['Pragma'] = 'no-cache';
      
      // Small delay to ensure header is propagated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Get user info with retry logic
      let userResponse;
      let retries = 3;
      
      for (let i = 0; i < retries; i++) {
        try {
          userResponse = await axios.get(`${API}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${access_token}`,
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          });
          break; // Success, exit retry loop
        } catch (err) {
          if (i === retries - 1) {
            // Last retry failed
            throw err;
          }
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 200));
          console.log(`Retry ${i + 1} fetching user info...`);
        }
      }
      
      // Clear old state and set new user
      setSubscription(null);
      setUsageStats(null);
      setUser(userResponse.data);
      
      console.log('Login successful for user:', userResponse.data.email);
      
      toast.success(`Welcome back, ${userResponse.data.name}!`);
      setAuthModalOpen(false);
      
      // Check for post-login redirect
      const redirectUrl = sessionStorage.getItem('postLoginRedirect');
      if (redirectUrl) {
        sessionStorage.removeItem('postLoginRedirect');
        // Use setTimeout to ensure state is updated before navigation
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 100);
      }
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
      console.error('Login error:', error);
      return { success: false, error: message };
    }
  };

  const verify = async (verificationData) => {
    try {
      await axios.post(`${API}/auth/verify`, verificationData);
      toast.success('Account verified successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Verification failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const resendVerification = async (emailOrPhone) => {
    try {
      await axios.post(`${API}/auth/resend-verification`, {
        email_or_phone: emailOrPhone
      });
      toast.success('Verification code sent!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send verification';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const fetchSubscriptionStatus = useCallback(async () => {
    if (subscriptionLoading) return; // Prevent multiple simultaneous calls
    
    try {
      setSubscriptionLoading(true);
      const response = await axios.get(`${API}/subscription-status`);
      setSubscription(response.data);
      setUsageStats(response.data.usage_stats);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  }, [subscriptionLoading]);

  const createPaymentOrder = async () => {
    try {
      const response = await axios.post(`${API}/create-payment-order`, {
        amount: 9900, // ₹99 in paise
        currency: 'INR',
        user_id: user.id
      });
      return response.data;
    } catch (error) {
      toast.error('Failed to create payment order');
      throw error;
    }
  };

  const verifyPayment = async (paymentData) => {
    try {
      const response = await axios.post(`${API}/verify-payment`, paymentData);
      toast.success('Payment successful! Premium activated!');
      await fetchSubscriptionStatus(); // Refresh subscription status
      return response.data;
    } catch (error) {
      toast.error('Payment verification failed');
      throw error;
    }
  };

  const canSaveRecipe = () => {
    if (!subscription || !usageStats) return true;
    return subscription.plan === 'premium' || usageStats.saved_recipes_count < 2;
  };

  const getRemainingRecipes = () => {
    if (!subscription || !usageStats) return 2;
    if (subscription.plan === 'premium') return 'unlimited';
    return Math.max(0, 2 - usageStats.saved_recipes_count);
  };

  const isAdmin = () => {
    // Check if user is admin (can be configured via backend)
    const adminEmail = process.env.REACT_APP_ADMIN_EMAIL || 'aaglobal2023@gmail.com';
    return user && user.email === adminEmail;
  };

  const logout = () => {
    // Clear all tokens from localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    
    // Clear axios authorization header
    delete axios.defaults.headers.common['Authorization'];
    
    // Clear all user-related state
    setUser(null);
    setSubscription(null);
    setUsageStats(null);
    
    // Clear any other localStorage items that might have user data
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      // Remove any keys that might contain user-specific data
      if (key && (key.includes('user') || key.includes('pet') || key.includes('purchase'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear sessionStorage as well
    sessionStorage.clear();
    
    // Show success message
    toast.success('Logged out successfully');
    
    // Force complete page reload with cache clearing
    setTimeout(() => {
      // Use location.replace to prevent going back
      window.location.replace('/');
      // Also try to reload to clear any cached content
      window.location.reload(true);
    }, 500);
  };

  const openAuthModal = (mode = 'login') => {
    setAuthMode(mode);
    setAuthModalOpen(true);
  };

  const closeAuthModal = () => {
    setAuthModalOpen(false);
  };

  const switchAuthMode = (mode) => {
    setAuthMode(mode);
  };

  const value = {
    user,
    loading,
    authModalOpen,
    authMode,
    showOnboarding,
    setShowOnboarding,
    subscription,
    usageStats,
    subscriptionLoading,
    signup,
    login,
    verify,
    resendVerification,
    logout,
    openAuthModal,
    closeAuthModal,
    switchAuthMode,
    refreshToken,
    fetchSubscriptionStatus,
    createPaymentOrder,
    verifyPayment,
    canSaveRecipe,
    getRemainingRecipes,
    isAdmin,
    checkAuthState,  // Expose this so GoogleAuthCallback can trigger re-check
    setUser  // Direct user setter for Google auth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};